import React, { useRef, useState, useEffect } from "react";
import { ThemeProvider } from "@mui/material/styles";
import {
  Button,
  Box,
  Input,
  TextField,
  Slider,
  Tooltip,
  Typography,
  Card,
} from "@mui/material";
import { StyledButton, theme } from "../StyledComponents";
import ImageRepository from "./ImageRepository";
import DrawingPadHelpInfoModal from "./DrawingPadHelpInfoModal";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { SketchPicker } from "react-color";
import { toast } from "react-toastify";
const DrawingPad = ({
  sectionName,
  activeSection,
  data,
  setData,
  doctorData,
}) => {
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushColor, setBrushColor] = useState("#000000");
  const [brushSize, setBrushSize] = useState(5);
  const [isAddingText, setIsAddingText] = useState(false);
  const [textInput, setTextInput] = useState("");
  const [helpDrawingPadModalOpen, setHelpDrawingPadModalOpen] = useState(false);
  const [textPosition, setTextPosition] = useState(null);
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [showPicker, setShowPicker] = useState(false);

  console.log("doctorData", doctorData?.department);

  useEffect(() => {
    if (!activeSection) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext("2d");
    if (!context) return;

    const width = canvas.parentElement.offsetWidth;
    const height = 300;
    canvas.width = width * 2;
    canvas.height = height * 2;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    context.scale(2, 2);
    context.lineCap = "round";
    context.strokeStyle = brushColor;
    context.lineWidth = brushSize;
    contextRef.current = context;

    saveCanvasState();
    drawCanvasBorder();
  }, [activeSection]);

  useEffect(() => {
    if (contextRef.current) {
      contextRef.current.strokeStyle = brushColor;
      contextRef.current.lineWidth = brushSize;
    }
  }, [brushColor, brushSize]);

  const drawCanvasBorder = () => {
    const canvas = canvasRef.current;
    const context = contextRef.current;
    if (!canvas || !context) return;

    context.save();
    context.strokeStyle = "#000000";
    context.lineWidth = 2;
    context.strokeRect(0, 0, canvas.width / 2, canvas.height / 2);
    context.restore();
  };

  const restoreCanvasState = (dataURL) => {
    const canvas = canvasRef.current;
    const context = contextRef.current;
    const img = new Image();
    img.src = dataURL;
    img.onload = () => {
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(img, 0, 0, canvas.width / 2, canvas.height / 2);
      drawCanvasBorder();
    };
  };

  const startDrawing = (event) => {
    if (isAddingText) return;
    event.preventDefault();
    const { offsetX, offsetY } = getEventCoordinates(event);
    contextRef.current.beginPath();
    contextRef.current.moveTo(offsetX, offsetY);
    setIsDrawing(true);
  };

  const finishDrawing = () => {
    if (!isDrawing) return;
    contextRef.current.closePath();
    saveCanvasState();
    setIsDrawing(false);
  };

  const draw = (event) => {
    event.preventDefault();
    if (!isDrawing || isAddingText) return;

    const { offsetX, offsetY } = getEventCoordinates(event);
    contextRef.current.lineTo(offsetX, offsetY);
    contextRef.current.stroke();
  };

  // const getEventCoordinates = (event) => {
  //   const canvas = canvasRef.current;
  //   if (!canvas) return { offsetX: 0, offsetY: 0 };

  //   const rect = canvas.getBoundingClientRect();
  //   if (event.nativeEvent instanceof TouchEvent) {
  //     const touch = event.nativeEvent.touches[0];
  //     return {
  //       offsetX: (touch.clientX - rect.left) * (canvas.width / rect.width),
  //       offsetY: (touch.clientY - rect.top) * (canvas.height / rect.height),
  //     };
  //   } else {
  //     return {
  //       offsetX: event.nativeEvent.offsetX,
  //       offsetY: event.nativeEvent.offsetY,
  //     };
  //   }
  // };
  const getEventCoordinates = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return { offsetX: 0, offsetY: 0 };

    const rect = canvas.getBoundingClientRect();

    if (event.nativeEvent instanceof TouchEvent) {
      const touch = event.nativeEvent.touches[0];
      return {
        offsetX:
          (((touch.clientX - rect.left) / rect.width) * canvas.width) / 2,
        offsetY:
          (((touch.clientY - rect.top) / rect.height) * canvas.height) / 2,
      };
    } else {
      return {
        offsetX: event.nativeEvent.offsetX,
        offsetY: event.nativeEvent.offsetY,
      };
    }
  };

  const saveCanvasState = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dataURL = canvas.toDataURL();
    setHistory((prevHistory) => [...prevHistory, dataURL]);
    setRedoStack([]);
  };

  const saveCanvas = () => {
    const canvas = canvasRef.current;
    const context = contextRef.current;

    // Create a temporary canvas with a white background
    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const tempContext = tempCanvas.getContext("2d");

    // Fill the background with white
    tempContext.fillStyle = "#FFFFFF";
    tempContext.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

    // Draw the original canvas content over the white background
    tempContext.drawImage(canvas, 0, 0);

    // Adjust the quality to ensure the image is more than 20 KB
    let quality = 1;
    let dataURL = tempCanvas.toDataURL("image/jpeg", quality); // Use JPEG for better compression control

    // Check if the image size is less than 20 KB
    const getBase64Size = (base64String) => {
      const strLength = base64String.length - (base64String.indexOf(",") + 1);
      return Math.ceil((strLength * 3) / 4);
    };

    // If image size is smaller than 20 KB, reduce quality
    while (getBase64Size(dataURL) < 20 * 1024 && quality > 0.1) {
      quality -= 0.1; // Decrease quality in steps
      dataURL = tempCanvas.toDataURL("image/jpeg", quality);
    }

    // Extract file properties
    const base64Data = dataURL.split(",")[1]; // Base64 part of the dataURL
    const sizeInBytes = getBase64Size(dataURL);

    // Create a file object
    const fileName = "OP-Image.jpg";
    const fileType = "image/jpeg";
    const rawFile = new File([base64Data], fileName, { type: fileType });

    // Append new details
    const newDetail = {
      name: fileName,
      type: fileType,
      size: sizeInBytes,
      base64: dataURL,
      rawFile: rawFile,
    };

    // Update the data array
    const newDetails = [...data, newDetail];
    console.log("newDetails", newDetails);
    setData(newDetails);
    toast.success(
      "Your drawing has been successfully saved. You can view it in the Attachments section."
    );
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const context = contextRef.current;
    if (!canvas || !context) return;

    context.clearRect(0, 0, canvas.width, canvas.height);
    drawCanvasBorder();
    saveCanvasState();
  };

  const undo = () => {
    if (history.length <= 1) return;
    const newHistory = [...history];
    const lastState = newHistory.pop();
    setRedoStack((prevRedo) => [...prevRedo, lastState]);
    setHistory(newHistory);
    restoreCanvasState(newHistory[newHistory.length - 1]);
  };

  const redo = () => {
    if (redoStack.length === 0) return;
    const newRedoStack = [...redoStack];
    const redoState = newRedoStack.pop();
    setHistory((prevHistory) => [...prevHistory, redoState]);
    setRedoStack(newRedoStack);
    restoreCanvasState(redoState);
  };

  const handleImageUpload = (imageUrl) => {
    const img = new Image();
    img.src = imageUrl;

    img.onload = () => {
      const canvas = canvasRef.current;
      const context = contextRef.current;
      if (!canvas || !context) return;

      const canvasWidth = canvas.width / 2;
      const canvasHeight = canvas.height / 2;

      const imgAspectRatio = img.width / img.height;
      const canvasAspectRatio = canvasWidth / canvasHeight;

      let imgWidth, imgHeight;
      if (imgAspectRatio > canvasAspectRatio) {
        imgWidth = canvasWidth;
        imgHeight = canvasWidth / imgAspectRatio;
      } else {
        imgHeight = canvasHeight;
        imgWidth = canvasHeight * imgAspectRatio;
      }

      const offsetX = (canvasWidth - imgWidth) / 2;
      const offsetY = (canvasHeight - imgHeight) / 2;

      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(img, offsetX, offsetY, imgWidth, imgHeight);
      drawCanvasBorder();
      saveCanvasState();
    };
  };

  const handleAddText = (event) => {
    const { offsetX, offsetY } = getEventCoordinates(event);
    setTextPosition({ x: offsetX, y: offsetY });
    if (textInput) {
      const context = contextRef.current;
      if (!context) return;

      context.fillStyle = brushColor;
      context.font = `${brushSize * 2}px Arial`;
      context.fillText(textInput, offsetX, offsetY);
      saveCanvasState();
      setTextInput("");
    }
  };

  // useEffect(() => {
  //   const handleTouchMove = (event) => event.preventDefault();
  //   document.addEventListener("touchmove", handleTouchMove, { passive: false });
  //   return () => document.removeEventListener("touchmove", handleTouchMove);
  // }, []);

  // useEffect(() => {
  //   const handleTouchMove = (event) => {
  //     // Allow scrolling if the event is outside the canvas
  //     if (!canvasRef.current.contains(event.target)) return;
  //     event.preventDefault(); // Prevent scrolling only inside the canvas
  //   };

  //   document.addEventListener("touchmove", handleTouchMove, { passive: false });
  //   return () => document.removeEventListener("touchmove", handleTouchMove);
  // }, []);

  useEffect(() => {
    const handleTouchMove = (event) => {
      if (!canvasRef.current || !canvasRef.current.contains(event.target))
        return;
      event.preventDefault(); // Prevent scrolling only inside the canvas
    };

    document.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      document.removeEventListener("touchmove", handleTouchMove);
    };
  }, []);

  return (
    <>
      <DrawingPadHelpInfoModal
        show={helpDrawingPadModalOpen}
        onHide={() => setHelpDrawingPadModalOpen(false)}
      />
      <ThemeProvider theme={theme}>
        {activeSection ? (
          <Box
            sx={{
              display: { md: "block", lg: "flex" },
              flexDirection: { md: "column", lg: "row" },
              alignItems: "flex-start",
              marginTop: "20px",
              marginBottom: "10px",
              height: "100%",
              gap: 2,
            }}
          >
            {/* Canvas + Buttons Card */}
            <Card
              sx={{
                borderRadius: "10px",
                boxShadow: "0 4px 4px 0 #C7C7CC inset",
                backgroundColor: "#FFF",
                width: { md: "100%", lg: "70%" },
                padding: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {/* Buttons Row */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: 2,
                  marginBottom: "10px",
                }}
              >
                <StyledButton
                  variant="contained"
                  bgColor="#FF9F54"
                  textColor="#171717"
                  onClick={clearCanvas}
                  sx={{ height: "50%" }}
                >
                  CLEAR
                </StyledButton>
                <StyledButton
                  variant="contained"
                  bgColor="#6AB9F7"
                  textColor="#FDFDFD"
                  onClick={saveCanvas}
                >
                  SAVE
                </StyledButton>
                <StyledButton
                  variant="contained"
                  bgColor="#E9E9E9"
                  textColor="#171717"
                  onClick={undo}
                >
                  UNDO
                </StyledButton>
                <StyledButton
                  variant="contained"
                  bgColor="#E9E9E9"
                  textColor="#171717"
                  onClick={redo}
                >
                  REDO
                </StyledButton>
                <StyledButton
                  variant="contained"
                  bgColor={isAddingText ? "error" : "#66BB6A"}
                  textColor="#FDFDFD"
                  onClick={() => setIsAddingText(!isAddingText)}
                >
                  {isAddingText ? "CANCEL TEXT" : "ADD TEXT"}
                </StyledButton>

                {isAddingText && (
                  <TextField
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    label="Enter Text"
                    size="small"
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "4px 8px", // shrink vertical padding
                        fontSize: "0.8rem",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.8rem", // shrink floating label
                      },
                      width: { xs: "100%", sm: "auto" },
                      minWidth: { sm: "200px" },
                    }}
                  />
                )}

                <Tooltip title="How to Use Drawing Pad">
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      margin: "auto",
                      gap: 4,
                    }}
                  >
                    <InfoOutlinedIcon
                      sx={{ color: "#21cdc0", fontSize: 20, cursor: "pointer" }}
                      onClick={() => setHelpDrawingPadModalOpen(true)}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#21cdc0",
                        fontWeight: "bold",
                        cursor: "pointer",
                      }}
                      onClick={() => setHelpDrawingPadModalOpen(true)}
                    >
                      HELP
                    </Typography>
                  </div>
                </Tooltip>
              </Box>

              {/* Controls Row */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: "center",
                  gap: 2,
                  marginBottom: 2,
                }}
              >
                <label
                  style={{
                    marginBottom: "1px",
                    color: "#808285",
                    fontWeight: 500,
                  }}
                >
                  {isAddingText ? "Text Size" : "Brush Stroke"}
                </label>
                <Slider
                  min={1}
                  max={20}
                  value={brushSize}
                  onChange={(e, newValue) => setBrushSize(newValue)}
                  sx={{ width: { xs: "100%", sm: "250px" } }}
                />
                <label
                  style={{
                    marginBottom: "1px",
                    color: "#808285",
                    fontWeight: 500,
                  }}
                >
                  {isAddingText ? "Text Color" : "Brush Color"}
                </label>
                {/* <input
                  type="color"
                  value={brushColor}
                  onChange={(e) => setBrushColor(e.target.value)}
                  style={{ width: "40px", height: "40px" }}
                /> */}
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid #ccc",
                      borderRadius: "20px",
                      padding: "4px 8px",
                      cursor: "pointer",
                      width: "60px",
                      justifyContent: "space-between",
                    }}
                    onClick={() => setShowPicker((prev) => !prev)}
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: "6px",
                        backgroundColor: brushColor,
                      }}
                    />
                    <ArrowDropDownIcon fontSize="small" />
                  </Box>

                  {showPicker && (
                    <Box sx={{ position: "absolute", zIndex: 1000 }}>
                      <SketchPicker
                        color={brushColor}
                        onChangeComplete={(color) => setBrushColor(color.hex)}
                      />
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Canvas */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "95%",
                  height: "300px",
                  border: "1px solid black",
                }}
              >
                <canvas
                  ref={canvasRef}
                  onMouseDown={isAddingText ? handleAddText : startDrawing}
                  onMouseMove={draw}
                  onMouseUp={finishDrawing}
                  onMouseLeave={finishDrawing}
                  onTouchStart={(event) => {
                    event.preventDefault();
                    isAddingText ? handleAddText(event) : startDrawing(event);
                  }}
                  onTouchMove={draw}
                  onTouchEnd={finishDrawing}
                />
              </Box>
            </Card>

            {/* Image Repository Card */}
            <Card
              sx={{
                borderRadius: "10px",
                boxShadow: "0 4px 4px 0 #C7C7CC inset",
                backgroundColor: "#FFF",
                width: { md: "100%", lg: "30%" },
                padding: 2,
              }}
            >
              <ImageRepository
                handleImageUpload={handleImageUpload}
                doctorDepartment={doctorData?.department}
              />
            </Card>
          </Box>
        ) : null}
      </ThemeProvider>
    </>
  );
};

export default DrawingPad;
